import client from './client';
import type {
  SurveillanceHorizon,
  SurveillancePolicyRecord,
  SurveillancePolicyDraft,
  SurveillanceAlertChannel,
  AlertChannelTestResult,
  FleetSurveillanceSummary,
  PolicyReadinessChecklistResponse,
  SurveillanceJob,
  HealthGuardSnapshot,
} from '../types/surveillance';

export async function listSurveillancePolicies(params?: {
  device_type?: string;
}): Promise<SurveillancePolicyRecord[]> {
  const { data } = await client.get('/api/v1/surveillance/policies', { params });
  return data as SurveillancePolicyRecord[];
}

export async function getSurveillancePolicyDefaults(): Promise<SurveillancePolicyDraft> {
  const { data } = await client.get('/api/v1/surveillance/policies/defaults');
  return data as SurveillancePolicyDraft;
}

export async function upsertSurveillancePolicy(
  payload: SurveillancePolicyDraft,
): Promise<SurveillancePolicyRecord> {
  const { data } = await client.post('/api/v1/surveillance/policies', payload);
  return data as SurveillancePolicyRecord;
}

export async function deleteSurveillancePolicy(policyId: number): Promise<void> {
  await client.delete(`/api/v1/surveillance/policies/${policyId}`);
}

export async function listAlertChannels(): Promise<SurveillanceAlertChannel[]> {
  const { data } = await client.get('/api/v1/surveillance/alerts/channels');
  return data as SurveillanceAlertChannel[];
}

export interface AlertChannelPayload {
  is_active: boolean;
  webhook_url?: string | null;
  webhook_headers?: Record<string, string> | null;
  email_recipients?: string[] | null;
  email_sender?: string | null;
}

export async function upsertAlertChannel(
  payload: AlertChannelPayload,
): Promise<SurveillanceAlertChannel> {
  const { data } = await client.post('/api/v1/surveillance/alerts/channels', payload);
  return data as SurveillanceAlertChannel;
}

export async function deleteAlertChannel(channelId: number): Promise<void> {
  await client.delete(`/api/v1/surveillance/alerts/channels/${channelId}`);
}

/** Sends one test alert to the organization's saved webhook now (admins only). */
export async function sendAlertChannelTest(): Promise<AlertChannelTestResult> {
  const { data } = await client.post('/api/v1/surveillance/alerts/channels/test');
  return data as AlertChannelTestResult;
}

export async function getFleetSurveillanceSummary(params?: {
  organization_id?: number;
  device_ids?: number[];
}): Promise<FleetSurveillanceSummary> {
  if (params?.device_ids && params.device_ids.length > 0) {
    const searchParams = new URLSearchParams();
    if (params.organization_id !== undefined) {
      searchParams.append('organization_id', String(params.organization_id));
    }
    params.device_ids.forEach((deviceId) => {
      searchParams.append('device_ids', String(deviceId));
    });
    const { data } = await client.get('/api/v1/surveillance/fleet-summary', {
      params: searchParams,
    });
    return data as FleetSurveillanceSummary;
  }
  const { data } = await client.get('/api/v1/surveillance/fleet-summary', { params });
  return data as FleetSurveillanceSummary;
}

export async function getHealthGuardSnapshot(): Promise<HealthGuardSnapshot> {
  const { data } = await client.get('/api/v1/surveillance/health-guard');
  return data as HealthGuardSnapshot;
}

export interface AutoTunePayload {
  reason: 'model_assigned' | 'manual' | 'drift';
  model_pack_id?: string | null;
  target_horizons?: SurveillanceHorizon[];
  override_health_guard?: boolean;
}

export async function triggerAutoTune(
  deviceId: number,
  payload: AutoTunePayload,
): Promise<SurveillanceJob> {
  const correlationId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `ui-${Date.now()}`;
  const { data } = await client.post(
    `/api/v1/surveillance/devices/${deviceId}/auto-tune`,
    payload,
    {
      headers: { 'x-correlation-id': correlationId },
    },
  );
  return data as SurveillanceJob;
}

export async function enableDetectionAndSeed(
  deviceId: number,
  seed_auto_tune = true,
): Promise<SurveillanceJob | { status: string }> {
  const { data } = await client.post(
    `/api/v1/surveillance/devices/${deviceId}/enable-detection`,
    { seed_auto_tune },
  );
  return data as SurveillanceJob | { status: string };
}

export async function getPolicyReadinessChecklist(): Promise<PolicyReadinessChecklistResponse> {
  const { data } = await client.get('/api/v1/surveillance/policies/readiness');
  return data as PolicyReadinessChecklistResponse;
}
